import { useQuery } from "@tanstack/react-query";
import apiClient from "@/app/api/client";
import { usePaginatedQuery } from "@/shared/hooks/usePaginatedQuery";
import { usePermission } from "@/shared/hooks/usePermission";
import type { EmployeeListItem } from "../types";
import type { AttendanceDayLike } from "../utils/summary";
import type { EmployeeFilters } from "./useEmployeeFilters";

/**
 * Lenta oynasi — PROFIL bilan AYNAN bir xil
 * (`components/EmployeeOverviewTab.tsx`). Oyna farq qilsa ro'yxat va profil
 * bitta xodim uchun ikki xil foiz ko'rsatardi, `summarise()` bir xil bo'lsa ham.
 */
export const WINDOW_DAYS = 30;

/**
 * Bugungi sana `YYYY-MM-DD` shaklida.
 *
 * ⚠️ Zona — BRAUZERNIKI, `settings.timezone` EMAS. Bu ONGLI
 * soddalashtirish va u profil sahifasidan BAYT-BAYT ko'chirilgan
 * (`components/EmployeeOverviewTab.tsx` dagi `bugun()`). U yerda funksiya
 * eksport qilinmagan va o'sha fayl F blokining hududi, shuning uchun
 * takrorlash ONGLI: semantikasi ajralib ketmasligi shart.
 */
export function bugun(): string {
	const now = new Date();

	return [
		now.getFullYear(),
		String(now.getMonth() + 1).padStart(2, "0"),
		String(now.getDate()).padStart(2, "0"),
	].join("-");
}

/**
 * Filtrlardan `GET /employees` so'rov parametrlari.
 *
 * ⚠️ `page`/`limit` BU YERGA KIRMAYDI — sahifalash `usePaginatedQuery` ning
 * o'z ishi va u parametrlarni o'zi qo'shadi.
 *
 * ⚠️ `riskFlagged` SATR sifatida ketadi (`"true"`), backend uni
 * `z.stringbool().optional()` bilan qabul qiladi. `false` da kalit umuman
 * uzatilmaydi — «filtr yo'q» jimgina «`risk_flag = false`» ga aylanmasligi
 * uchun.
 */
export function buildEmployeeParams(filters: EmployeeFilters): Record<string, string> {
	const params: Record<string, string> = {};

	if (filters.q) {
		params.q = filters.q;
	}
	if (filters.departmentId) {
		params.departmentId = filters.departmentId;
	}
	if (filters.positionId) {
		params.positionId = filters.positionId;
	}
	if (filters.hrStatus) {
		params.hrStatus = filters.hrStatus;
	}
	if (filters.zoneId) {
		params.zoneId = filters.zoneId;
	}
	if (filters.riskFlagged === true) {
		params.riskFlagged = "true";
	}

	return params;
}

/**
 * Ro'yxat so'rovi.
 *
 * ⚠️ `queryKey` `["employees"]` bo'lib QOLADI — `useCreateEmployee` va
 * `useUpdateEmployee` (`useEmployees.ts`) aynan shu prefiks bo'yicha
 * invalidatsiya qiladi; boshqa prefiks yozilsa yangi xodim ro'yxatda
 * ko'rinmasdi.
 *
 * ⚠️⚠️ **`filters.page`/`filters.limit` HOZIRCHA UZATILMAYDI.**
 * `usePaginatedQuery` sahifa holatini O'Z ichida (`useState`) saqlaydi va
 * boshqariladigan rejimni (`page`/`limit`/`onPageChange` proplari) qo'llab
 * -quvvatlamaydi. Uni qo'shish `shared/hooks/usePaginatedQuery.ts` ga
 * tegishni talab qiladi — faylni 30 dan ortiq modul import qiladi, ya'ni u
 * poydevor bosqichining ishi va parallel tarmoqlar unga tegsa merge
 * to'qnashardi.
 *
 * ⚠️⚠️ **Buning IKKI oqibati bor va faqat BIRI zararsiz:**
 * (a) zararsiz — profildan «orqaga» qaytganda jadval 1-sahifadan
 *     boshlanadi (`?page=3` URL da qoladi, lekin qo'llanmaydi);
 * (b) ZARARLI — filtr o'zgarganda ichki sahifa N da qolib, so'rov
 *     `["employees", N, 20, {…}]` kalitiga tushardi va foydalanuvchi bo'sh
 *     jadval ko'rardi. Buni `EmployeesPage` qoplaydi: filtr `onChange`
 *     ida `query.setPage(1)` ham chaqiriladi (qorovul —
 *     `EmployeesPage.test.tsx` dagi «filtr o'zgarganda jadval 1-sahifaga
 *     qaytadi»).
 *
 * Filtrlarning O'ZI (bo'lim, lavozim, holat, hudud, xavf, qidiruv) URL da
 * to'liq saqlanadi va `parseEmployeeFilters` `page`/`limit` ni baribir
 * o'qiydi — ya'ni `usePaginatedQuery` boshqariladigan bo'lgan kunda bu
 * yerga uch qator qo'shilib, `EmployeesPage` dagi qoplama olib tashlanadi.
 */
export function useEmployeeRows(filters: EmployeeFilters) {
	const params = buildEmployeeParams(filters);

	return usePaginatedQuery<EmployeeListItem>({
		queryKey: ["employees"],
		url: "/employees",
		params: Object.keys(params).length > 0 ? params : undefined,
	});
}

interface AttendanceCohortResponse {
	items: Array<{ employeeId: string; days: AttendanceDayLike[] }>;
}

/**
 * Sahifadagi xodimlarning 30 kunlik davomat kunlari — BITTA so'rovda.
 *
 * ⚠️⚠️ **Kesh kaliti `"attendance-days"` bilan BOSHLANADI**, `"employees"`
 * bilan emas. `useCorrectAttendanceDay` (`modules/attendance`) kun
 * tuzatilganda `invalidateQueries({ queryKey: ["attendance-days"] })`
 * chaqiradi, TanStack Query esa kalitni BOSHIDAN prefiks bo'yicha
 * solishtiradi — boshqa prefiks bu invalidatsiyaga mos kelmasdi va
 * tuzatishdan keyin ro'yxat eski lentani ko'rsatib turaverardi (jimgina).
 * Dalil va aynan shu qaror: `useEmployees.ts` ning `useEmployeeAttendanceDays` i.
 *
 * ⚠️ `ids` vergul bilan birlashtiriladi va kesh kalitida ham SHU SATR
 * ishlatiladi — massivni to'g'ridan-to'g'ri kalitga qo'yish har renderda
 * yangi havola berib, `queryKey` ni beqaror qilardi.
 *
 * ⚠️ `ids` — sahifadagi qatorlar, ya'ni eng ko'pi 100 ta (`LIMITS` ning
 * maksimumi). Bu marshrutning `SUMMARY_MAX_EMPLOYEES = 100` chegarasi bilan
 * AYNAN mos.
 *
 * ⚠️ `can(...)` — `usePermission()` PARAMETR OLMAYDI va OBYEKT qaytaradi;
 * `enabled: usePermission("attendance","read") && …` doim truthy bo'lib,
 * ruxsatsiz foydalanuvchi baribir so'rov yuborardi.
 *
 * ⚠️⚠️ Natija «yo'q» ni «yuklanmoqda» dan AJRATMAYDI — bu farqni
 * iste'molchi `query.data === undefined` bilan qiladi (`Map.has()` bilan
 * EMAS). Shuning uchun hookka `hasRow()` kabi qulaylik QO'SHILMAYDI.
 */
export function useEmployeeAttendanceCohort(ids: string[], from: string, to: string) {
	const { can } = usePermission();
	const joined = ids.join(",");

	return useQuery({
		queryKey: ["attendance-days", "cohort", from, to, joined],
		// ⚠️⚠️ Wire → `Map` aylantirish `select` da, `queryFn` da EMAS: aks
		// holda KESHDA `Map` yotardi va testdagi `setQueryData` urug'i ham
		// `Map` qurishga majbur bo'lardi, mavjud ro'yxat urug'i esa WIRE
		// shaklida solinadi. Ikki xil urug'lash naqshi «testda ishlaydi,
		// brauzerda yo'q» sinfidagi tafovutni tug'diradi.
		queryFn: async () => {
			const response = await apiClient.get<{ success: true; data: AttendanceCohortResponse }>(
				"/attendance/summary",
				{ params: { employeeIds: joined, from, to } }
			);

			return response.data.data;
		},
		select: (data: AttendanceCohortResponse) =>
			new Map(data.items.map((row) => [row.employeeId, row.days])),
		enabled: can("attendance", "read") && ids.length > 0,
		staleTime: 60_000,
	});
}

export interface CohortZone {
	id: string;
	name: string;
	tur: "asosiy" | "vaqtinchalik";
}

interface ZoneCohortResponse {
	items: Array<{ employeeId: string; zones: CohortZone[] }>;
}

/**
 * Sahifadagi xodimlarning BUGUN AMALDAGI hududlari.
 *
 * ⚠️ Doiradan tashqaridagi id lar javobdan JIMGINA tashlanadi (404 emas —
 * bu batch so'rov), ya'ni `items` so'ralgan id lardan QISQA bo'lishi
 * NORMAL. Iste'molchi buni «hali yuklanmadi» bilan ARALASHTIRMASLIGI
 * kerak: yuklanish belgisi `query.data === undefined`, hudud yo'qligi esa
 * `map.get(id) ?? []`.
 *
 * ⚠️ Xodimda bir nechta amaldagi hudud bo'lishi mumkin (`asosiy` +
 * `vaqtinchalik`) — javob MASSIV beradi.
 */
export function useEmployeeZoneCohort(ids: string[]) {
	const { can } = usePermission();
	const joined = ids.join(",");

	return useQuery({
		queryKey: ["zones", "cohort", joined],
		queryFn: async () => {
			const response = await apiClient.get<{ success: true; data: ZoneCohortResponse }>(
				"/zones/assignments",
				{ params: { employeeIds: joined } }
			);

			return response.data.data;
		},
		select: (data: ZoneCohortResponse) =>
			new Map(data.items.map((row) => [row.employeeId, row.zones])),
		enabled: can("zones", "read") && ids.length > 0,
		staleTime: 60_000,
	});
}

export interface ZoneOption {
	id: string;
	name: string;
}

/**
 * «Hudud» filtri uchun variantlar.
 *
 * ⚠️ **`modules/reports` dan `useZoneOptions` IMPORT QILINMAYDI** — modul
 * chegarasi: `useEmployees.ts` ning `useDepartmentOptions` i («boshqa
 * modulning servisini import qilish o'rniga to'g'ridan-to'g'ri `apiClient`
 * orqali») va `useEmployeeAttendanceDays` i («`modules/attendance` ning
 * hook'i import QILINMAYDI») bir xil qarorni ikki marta yozib qo'ygan.
 *
 * ⚠️ Bankda hudud 100 tadan oshsa ro'yxat QISMAN to'ladi — jonli
 * xaritadagi bir xil cheklov (`/zones` `limit` maksimumi 100).
 *
 * ⚠️ `enabled: can("zones", "read")` — variantlarni iste'mol qiladigan
 * `Select` allaqachon `PermissionGate` ostida, ya'ni ruxsatsiz
 * foydalanuvchi uchun so'rov FAQAT 403 keltirardi va hech narsa
 * ko'rsatmasdi.
 */
export function useZoneOptions() {
	const { can } = usePermission();

	return useQuery({
		queryKey: ["zones", "options"],
		queryFn: async () => {
			const response = await apiClient.get<{
				success: true;
				data: { items: ZoneOption[] };
			}>("/zones", { params: { page: 1, limit: 100 } });

			return response.data.data.items;
		},
		enabled: can("zones", "read"),
		staleTime: 60_000,
	});
}
